package com.shadowings.gero.data.repository

import com.shadowings.gero.data.lessons.DistractorPool
import com.shadowings.gero.domain.model.ExplanationContent
import com.shadowings.gero.domain.model.UniliteralExplanation
import com.shadowings.gero.domain.repository.LessonContentRepository
import com.shadowings.gero.domain.repository.UserDataRepository
import com.shadowings.gero.util.systemLanguageCode
import kotlinx.coroutines.flow.first

data class SentenceResult(
    val hieroglyphs: String,
    val transliteration: String,
    val translationIt: String,
    val translationEn: String,
    val translationDe: String,
    val translationEs: String,
    val translationFr: String,
    val isNegative: Boolean = false,
    val thisSubject: Boolean = false,
    // Language code → other word orders that are just as right ("il buon figlio" next to "il
    // figlio buono"). Only sentences with an adjective have any.
    val alternativeTranslations: Map<String, List<String>> = emptyMap(),
    val hasAdjective: Boolean = false
)

class SentenceRepository(
    private val userDataRepository: UserDataRepository,
    private val lessonContentRepository: LessonContentRepository
) {

    private class SeenContent(
        val completedLessonIds: Set<String>,
        val languageCode: String,
        val wordIds: Set<String>,
        val quizOnlyWordIds: Set<String>,
        val hieroCodes: Set<String>
    )

    private var seenContentCache: SeenContent? = null

    fun invalidateSeenWordsCache() {
        seenContentCache = null
    }

    // This is an app-wide single, so the cache is also keyed on the completed
    // lessons: finishing a lesson changes that set and forces a recompute,
    // instead of serving the set from before it until the app restarts.
    private suspend fun getSeenContent(): SeenContent {
        val userData = userDataRepository.userData.first()
        val languageCode = userData.language.ifBlank { systemLanguageCode() }
        val completedLessonIds = userData.stars.filterValues { it > 0 }.keys
        seenContentCache
            ?.takeIf { it.completedLessonIds == completedLessonIds && it.languageCode == languageCode }
            ?.let { return it }
        val computed = computeSeenContent(completedLessonIds, languageCode)
        seenContentCache = computed
        return computed
    }

    private suspend fun getSeenWordIds(): Set<String> = getSeenContent().wordIds

    // Mirrors CompendioScreen's runtime "seen words" computation: a word counts
    // as seen once the player has completed (stars > 0) a lesson whose
    // explanation content introduces it.
    private fun computeSeenContent(completedLessonIds: Set<String>, languageCode: String): SeenContent {
        val words = DatabaseRepositoryImpl.words
        val hieros = DatabaseRepositoryImpl.hieros

        val seenWords = mutableSetOf<String>()
        val quizOnlyWords = mutableSetOf<String>()
        val seenHieros = mutableSetOf<String>()
        for (lessonId in completedLessonIds) {
            quizOnlyWords.addAll(lessonContentRepository.getQuizOnlyWordIds(lessonId))
            val explanations =
                lessonContentRepository.getExplanationContent(lessonId, hieros, words, languageCode)
            for (explanation in explanations) {
                when (explanation) {
                    is ExplanationContent -> explanation.wordId?.let { seenWords.add(it) }
                    is UniliteralExplanation -> seenHieros.add(explanation.id)
                }
            }
        }
        return SeenContent(completedLessonIds, languageCode, seenWords, quizOnlyWords, seenHieros)
    }

    // Distractor pool for a quiz of [currentLessonId]: everything the player
    // has already seen (quiz-only words included, as in the Compendio) plus
    // what the current lesson introduces, so it's never empty for the lesson
    // being played.
    suspend fun getDistractorPool(currentLessonId: String): DistractorPool {
        val seen = getSeenContent()
        val words = DatabaseRepositoryImpl.words
        val hieros = DatabaseRepositoryImpl.hieros
        val currentExplanations = lessonContentRepository.getExplanationContent(
            currentLessonId, hieros, words, seen.languageCode
        )
        val wordIds = seen.wordIds + seen.quizOnlyWordIds +
                lessonContentRepository.getQuizOnlyWordIds(currentLessonId) +
                currentExplanations.filterIsInstance<ExplanationContent>().mapNotNull { it.wordId }
        val hieroCodes = seen.hieroCodes +
                currentExplanations.filterIsInstance<UniliteralExplanation>().map { it.id }
        return DistractorPool(wordIds = wordIds, hieroCodes = hieroCodes)
    }

    // For callers with their own pattern-selection logic (the old chapter11
    // getRandomPseudoverbalSentence/buildPseudoverbalObjectSentence/
    // buildPseudoverbalComplexObjectSentence, and VsoSentenceUtils.kt) that
    // should keep choosing among candidate rows themselves — this just gives
    // them the same rows with every candidate id trimmed down to "seen" ones,
    // instead of a single picked row. A row with no seen subject or verb left
    // is dropped since every sentence needs both; objects/possessors can
    // still end up empty.
    suspend fun getSeenFilteredVerbSentences(): List<SentenceOptions> {
        val seenWordIds = getSeenWordIds()
        return DatabaseRepositoryImpl.verbSentences
            .map { row ->
                row.copy(
                    subjects = row.subjects.filter { isSeen(it, seenWordIds) },
                    verbs = row.verbs.filter { isSeen(it, seenWordIds) },
                    objects = row.objects.filter { isSeen(it, seenWordIds) },
                    possessors = row.possessors.filter { isSeen(it, seenWordIds) },
                    hrPlaceComplements = row.hrPlaceComplements.filter { isSeen(it, seenWordIds) },
                    hrDistributionComplements = row.hrDistributionComplements.filter { isSeen(it, seenWordIds) },
                    mPlaceComplements = row.mPlaceComplements.filter { isSeen(it, seenWordIds) }
                )
            }
            .filter { it.subjects.isNotEmpty() && it.verbs.isNotEmpty() }
    }

    // A word counts as seen if its own id is in the seen set, or it's a
    // flessione (plural/feminine) of one that is — e.g. GATTI isn't seen on
    // its own, but its base_word GATTO might be.
    private fun isSeen(id: String, seenWordIds: Set<String>): Boolean {
        if (id in seenWordIds) return true
        val baseId =
            DatabaseRepositoryImpl.words.firstOrNull { it.id == id }?.baseWordId ?: return false
        return baseId in seenWordIds
    }
}
